import {Injectable} from '@angular/core';
import {instanceToPlain, plainToInstance} from "class-transformer";
import {ToastrService} from "ngx-toastr";
import {Guid} from "common-shared";
import {FilterSpecsBySourcePipe} from "../../common/specs/filter-specs-by-source.pipe";
import {SpecLineModel} from "../../properties/models/spec-line.model";
import {MasterOperationModel} from "../../manufacturing/models/master-operation.model";
import {
  ProcessSelectorModel,
  ProcessSelectorWithOperationsModel
} from "../../manufacturing/models/process-selector.model";
import {TaskSelectorModel, TaskSelectorWithInstructionsModel} from "../../manufacturing/models/task-selector.model";
import {
  MasterInputInstructionModel,
  MasterOutputInstructionModel
} from "../../manufacturing/models/master-input-instruction.model";
import {MasterUseInstructionModel} from "../../manufacturing/models/master-use-instruction.model";
import {CostPriceComponentPriceModel} from "../../manufacturing/models/cost-price-component-price.model";

export type ObjectTypeForCopy =
  'ProcessSelector'
  | 'TaskSelector'
  | 'MasterOperation'
  | 'MasterInputInstruction'
  | 'MasterUseInstruction'
  | 'MasterOutputInstruction'
  | 'MasterInstruction'
  | 'Spec'
  | 'CostPriceComponentPrice';

export type ObjectTypeForPaste =
  'ProcessSelector'
  | 'TaskSelector'
  | 'MasterOperation'
  | 'MasterInstruction'
  | 'Spec'
  | 'CostPriceComponentPrice';

type Content =
  MasterOperationModel[]
  | ProcessSelectorWithOperationsModel[]
  | TaskSelectorWithInstructionsModel[]
  | MasterInputInstructionModel[]
  | MasterUseInstructionModel[]
  | MasterOutputInstructionModel[]
  | SpecLineModel[]
  | CostPriceComponentPriceModel[];

interface ICopiedContent {
  type: ObjectTypeForCopy;
  content: string;
}

interface IPastedContent {
  type: ObjectTypeForCopy;
  content: Content;
}

@Injectable({
  providedIn: 'root'
})
export class CopyPasteService {
  copiedContentKey = 'ime-copied-content';
  toastSettings = {disableTimeOut: false, timeOut: 5000, positionClass: 'toast-bottom-left'};

  constructor(private toastrService: ToastrService, private filterSpecsBySource: FilterSpecsBySourcePipe) {
  }

  public copy(type: ObjectTypeForCopy, content: Content) {
    const copyContent = {
      type: type,
      content: JSON.stringify(instanceToPlain(content))
    };

    const stringifiedContent = JSON.stringify(copyContent);
    navigator.clipboard?.writeText(stringifiedContent);
    localStorage.setItem(this.copiedContentKey, stringifiedContent);
    this.toastrService.success(this.getCopyMessage(type, content), null, this.toastSettings);
  }

  private getCopyMessage(type: ObjectTypeForCopy, content: Content): string {
    if (type === 'Spec') {
      if (content.length === 0) {
        return 'Turn on Selection column and mark the specs you want to copy.'
      }
      const amount = content.length === 1 ? '1 spec' : `${content.length} specs`;
      return `${amount} copied`;
    } else if (type === 'CostPriceComponentPrice') {
      if (content.length === 0) {
        return 'You have to mark the rows you want to copy.'
      }
      const amount = content.length === 1 ? '1 cost price component' : `${content.length} cost price components`;
      return `${amount} copied`;
    } else {
      const codes = content.map(value => value.Code ?? value.TaskSelector?.Code ?? value.ProcessSelector?.Code)?.join(', ');
      return `${codes} copied`;
    }
  }

  public paste(type: ObjectTypeForPaste, mo: MasterOperationModel, ps: ProcessSelectorModel, ts: TaskSelectorModel, fromClipBoard: boolean = false): Promise<IPastedContent> {
    let allowedTypes = CopyPasteService.getAllowedTypes(type);

    return this.getCopiedContent(allowedTypes, fromClipBoard).then(copiedContent => {
      if (copiedContent == null) return null;
      let content;
      switch (type) {
        case 'MasterInstruction':
          content = this.pasteMasterInstruction(copiedContent, mo, ts);
          break;
        case 'TaskSelector':
          content = this.pasteTaskSelector(copiedContent, mo);
          break;
        case 'MasterOperation':
          content = this.pasteMasterOperation(copiedContent, ps);
          break;
        case 'ProcessSelector':
          content = this.pasteProcessSelector(copiedContent);
          break;
        case 'Spec':
          content = this.pasteSpec(copiedContent);
          break;
        case 'CostPriceComponentPrice':
          content = this.pasteCpc(copiedContent);
          break;
      }
      return {type: copiedContent.type, content: content};
    });
  }

  private static getAllowedTypes(type: ObjectTypeForCopy | ObjectTypeForPaste) {
    let allowedTypes = [] as ObjectTypeForCopy[];
    switch (type) {
      case 'MasterInstruction':
        allowedTypes = ['MasterInputInstruction', 'MasterUseInstruction', 'MasterOutputInstruction', 'TaskSelector'];
        break;
      case 'TaskSelector':
        allowedTypes = ['TaskSelector'];
        break;
      case 'MasterOperation':
        allowedTypes = ['MasterOperation', 'ProcessSelector'];
        break;
      case 'ProcessSelector':
        allowedTypes = ['ProcessSelector'];
        break;
      case 'Spec':
        allowedTypes = ['Spec'];
        break;
      case 'CostPriceComponentPrice':
        allowedTypes = ['CostPriceComponentPrice'];
        break;
    }
    return allowedTypes;
  }

  pasteMasterOperation(copiedContent: ICopiedContent, ps: ProcessSelectorModel) {
    switch (copiedContent.type) {
      case 'MasterOperation':
        let masterOperationModels = JSON.parse(copiedContent.content).map(value => plainToInstance(MasterOperationModel, value)) as MasterOperationModel[];
        this.prepareMasterOperationsForPaste(masterOperationModels, ps);
        return masterOperationModels;
      case 'ProcessSelector':
        return this.pasteProcessSelector(copiedContent);
    }
  }

  pasteProcessSelector(copiedContent: ICopiedContent): ProcessSelectorWithOperationsModel[] {
    let processSelectorsWithOperations = JSON.parse(copiedContent.content).map(value => plainToInstance(ProcessSelectorWithOperationsModel, value)) as ProcessSelectorWithOperationsModel[];
    this.prepareProcessSelectorsForPaste(processSelectorsWithOperations);
    return processSelectorsWithOperations;
  }

  pasteTaskSelector(copiedContent: ICopiedContent, masterOperation: MasterOperationModel): TaskSelectorWithInstructionsModel[] {
    let taskSelectorsWithInstructions = JSON.parse(copiedContent.content).map(value => plainToInstance(TaskSelectorWithInstructionsModel, value)) as TaskSelectorWithInstructionsModel[];
    this.prepareTaskSelectorsForPaste(taskSelectorsWithInstructions);
    masterOperation.TaskSelectors = masterOperation.TaskSelectors?.length
      ? [...masterOperation.TaskSelectors.concat(taskSelectorsWithInstructions.map(value => value.TaskSelector))]
      : [...taskSelectorsWithInstructions.map(value => value.TaskSelector)];
    masterOperation.MasterInputInstructions = [...masterOperation.MasterInputInstructions.concat(...taskSelectorsWithInstructions.map(value => value.MasterInputInstructions))];
    masterOperation.MasterUseInstructions = [...masterOperation.MasterUseInstructions.concat(...taskSelectorsWithInstructions.map(value => value.MasterUseInstructions))];
    masterOperation.MasterOutputInstructions = [...masterOperation.MasterOutputInstructions.concat(...taskSelectorsWithInstructions.map(value => value.MasterOutputInstructions))];
    return taskSelectorsWithInstructions;
  }

  pasteMasterInstruction(copiedContent: ICopiedContent, masterOperation: MasterOperationModel, taskSelector: TaskSelectorModel) {
    switch (copiedContent.type) {
      case 'MasterInputInstruction':
        let masterInputInstructionModels = JSON.parse(copiedContent.content).map(value => plainToInstance(MasterInputInstructionModel, value));
        this.prepareMasterInstructionsForPaste(masterInputInstructionModels, taskSelector);
        masterOperation.MasterInputInstructions = [...masterOperation.MasterInputInstructions, ...masterInputInstructionModels];
        return masterInputInstructionModels;
      case 'MasterUseInstruction':
        let masterUseInstructionModels = JSON.parse(copiedContent.content).map(value => plainToInstance(MasterUseInstructionModel, value));
        this.prepareMasterInstructionsForPaste(masterUseInstructionModels, taskSelector);
        masterOperation.MasterUseInstructions = [...masterOperation.MasterUseInstructions, ...masterUseInstructionModels];
        return masterUseInstructionModels;
      case 'MasterOutputInstruction':
        let masterOutputInstructionModels = JSON.parse(copiedContent.content).map(value => plainToInstance(MasterOutputInstructionModel, value));
        this.prepareMasterInstructionsForPaste(masterOutputInstructionModels, taskSelector);
        masterOperation.MasterOutputInstructions = [...masterOperation.MasterOutputInstructions, ...masterOutputInstructionModels];
        return masterOutputInstructionModels;
      case 'TaskSelector':
        return this.pasteTaskSelector(copiedContent, masterOperation);
      default:
        return [];
    }
  }

  pasteSpec(copiedContent: ICopiedContent): SpecLineModel[] {
    let specLineModels = JSON.parse(copiedContent.content).map(value => plainToInstance(SpecLineModel, value)) as SpecLineModel[];
    CopyPasteService.prepareSpecsForPaste(specLineModels);
    return specLineModels;
  }

  pasteCpc(copiedContent: ICopiedContent): CostPriceComponentPriceModel[] {
    let cpcs = JSON.parse(copiedContent.content).map(value => plainToInstance(CostPriceComponentPriceModel, value)) as CostPriceComponentPriceModel[];
    CopyPasteService.prepareCpcsForPaste(cpcs);
    return cpcs;
  }

  prepareMasterInstructionsForPaste(masterInstructionModels: MasterInputInstructionModel[] | MasterUseInstructionModel[] | MasterOutputInstructionModel[], taskSelector: TaskSelectorModel) {
    for (let masterInstructionModel of masterInstructionModels) {
      this.prepareMasterInstructionForPaste(masterInstructionModel, taskSelector);
    }
  }

  prepareMasterInstructionForPaste(masterInstructionModel: MasterInputInstructionModel | MasterUseInstructionModel | MasterOutputInstructionModel, taskSelector: TaskSelectorModel) {
    masterInstructionModel.Specs = this.filterSpecsBySource.transform(masterInstructionModel.Specs, masterInstructionModel);
    masterInstructionModel.Id = Guid.create();
    masterInstructionModel.MasterOperationId = null;
    masterInstructionModel.ParentId = null;
    masterInstructionModel.TaskSelectorId = taskSelector?.Id ?? undefined;
    masterInstructionModel.ParentId = null;
    CopyPasteService.prepareSpecsForPaste(masterInstructionModel.Specs);
    return masterInstructionModel;
  }


  private prepareMasterOperationsForPaste(masterOperationModels: MasterOperationModel[], processSelectorModel: ProcessSelectorModel) {
    for (let masterOperationModel of masterOperationModels) {
      masterOperationModel.Id = Guid.create();
      masterOperationModel.ParentId = null;
      masterOperationModel.ParentProcessValidCondition = null;
      masterOperationModel.ParentValidCondition = null;
      masterOperationModel.ProcessSelectorId = processSelectorModel?.Id;
      CopyPasteService.prepareSpecsForPaste(masterOperationModel.Specs);

      if (masterOperationModel.TaskSelectors?.length) {
        for (let taskSelector of masterOperationModel.TaskSelectors) {
          let taskSelectorOldId = taskSelector.Id;
          taskSelector.Id = Guid.create();
          this.prepareMasterInstructionsForPaste(masterOperationModel.MasterInputInstructions.filter(value => value.TaskSelectorId === taskSelectorOldId), taskSelector);
          this.prepareMasterInstructionsForPaste(masterOperationModel.MasterUseInstructions.filter(value => value.TaskSelectorId === taskSelectorOldId), taskSelector);
          this.prepareMasterInstructionsForPaste(masterOperationModel.MasterOutputInstructions.filter(value => value.TaskSelectorId === taskSelectorOldId), taskSelector);
          CopyPasteService.prepareSpecsForPaste(taskSelector.Specs);
        }
      }

      this.prepareMasterInstructionsForPaste(masterOperationModel.MasterInputInstructions.filter(value => !value.TaskSelectorId), undefined);
      this.prepareMasterInstructionsForPaste(masterOperationModel.MasterUseInstructions.filter(value => !value.TaskSelectorId), undefined);
      this.prepareMasterInstructionsForPaste(masterOperationModel.MasterOutputInstructions.filter(value => !value.TaskSelectorId), undefined);
    }
  }

  private prepareTaskSelectorsForPaste(taskSelectorsWithInstructions: TaskSelectorWithInstructionsModel[]) {
    for (let taskSelectorWithInstructions of taskSelectorsWithInstructions) {
      taskSelectorWithInstructions.TaskSelector.Id = Guid.create();
      CopyPasteService.prepareSpecsForPaste(taskSelectorWithInstructions.TaskSelector.Specs);
      this.prepareMasterInstructionsForPaste(taskSelectorWithInstructions.MasterInputInstructions, taskSelectorWithInstructions.TaskSelector);
      this.prepareMasterInstructionsForPaste(taskSelectorWithInstructions.MasterUseInstructions, taskSelectorWithInstructions.TaskSelector);
      this.prepareMasterInstructionsForPaste(taskSelectorWithInstructions.MasterOutputInstructions, taskSelectorWithInstructions.TaskSelector);
    }
  }

  prepareProcessSelectorsForPaste(processSelectorsWithOperations: ProcessSelectorWithOperationsModel[]) {
    for (let processSelectorWithOperations of processSelectorsWithOperations) {
      processSelectorWithOperations.ProcessSelector.Id = Guid.create();
      CopyPasteService.prepareSpecsForPaste(processSelectorWithOperations.ProcessSelector.Specs);
      this.prepareMasterOperationsForPaste(processSelectorWithOperations.MasterOperations, processSelectorWithOperations.ProcessSelector);
    }
  }

  private static prepareSpecsForPaste(specs: SpecLineModel[]) {
    for (let spec of specs) {
      spec.LineId = spec.MappingId;
      spec.MappingSource = spec.LineSource = null;
      spec.Id = Guid.create();
    }
  }

  private static prepareCpcsForPaste(cpcs: CostPriceComponentPriceModel[]) {
    for (let cpc of cpcs) {
      cpc.Id = Guid.create();
    }
  }

  private async getCopiedContent(allowedTypes: ObjectTypeForCopy[], fromClipboard: boolean = false): Promise<ICopiedContent> {
    const content = fromClipboard ? await this.getFromClipboard() : localStorage.getItem(this.copiedContentKey);
    if (content === null) {
      return null;
    }
    let parsedContent: ICopiedContent;

    try {
      parsedContent = JSON.parse(content) as ICopiedContent;
    } catch (e) {
      this.toastrService.error(`Data not suitable for paste`, null, this.toastSettings);
      return null
    }

    if (allowedTypes.includes(parsedContent.type)) {
      return parsedContent;
    } else {
      this.toastrService.error(`Not allowed to paste content of type "${parsedContent.type}" here`, null, this.toastSettings);
    }
    return null;
  }

  async getFromClipboard() {
    const clipboardPermission = await navigator.permissions.query({name: "clipboard-read" as PermissionName});
    if (clipboardPermission.state === "granted" || clipboardPermission.state === "prompt") {
      return navigator.clipboard.readText();
    } else {
      this.toastrService.error(`No permission to access clipboard`, null, this.toastSettings);
      return null;
    }
  }
}
